import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  arr_map,
  data_get,
  fallback,
  OptionalValue,
  str_snake_case,
} from '../../../utils/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'fieldmagic-legend-indicators',
  template: `
    <ng-container *ngIf="legends$ | async as legends">
      <div
        *ngIf="legends | filled"
        class="container-fluid"
      >
        <div
          fieldmagicGridRow
          class="w-100"
        >
          <ng-container *ngFor="let legend of legends; trackBy: tracker; let index = index;">
            <div
              [fieldmagicGridColumn]="legends.length > 12 ? 3 : 2"
              class="fieldmagic-legend-container pointer pl-0 pr-0 pb-0 pt-0"
              [class.pr-1]="legends.length > 1 && index !== (legends.length - 1)"
              (click)="onClick(legend)"
            >
              <div
                class="w-100 rounded h-30"
                [style.background-color]="legend.color"
                [style.color]="legend.text_color"
              >
                <div class="h-100">
                  <div class="row ml-0 mr-0">
                    <div
                      class="col-xl-3 col-lg-3 col-md-12 col-sm-3 col-xs-3 col-3 fieldmagic-legend-count text-center border-right border-white"
                      [style.color]="legend.text_color"
                    >
                      {{ legend.count | fallback : legend.default_count }}
                    </div>

                    <div
                      class="col-xl-9 col-lg-9 col-md-12 col-sm-9 col-xs-9 col-9 fieldmagic-legend-text text-center border-white"
                      [style.color]="legend.text_color"
                    >
                      {{ legend.label | translate }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
    </ng-container>
  `,
})
export class FieldmagicLegendIndicatorsComponent implements OnInit, OnDestroy {
  readonly legends$ = new BehaviorSubject<FieldmagicInternalLegendDef[]>([]);

  @Input()
  set legends(values: FieldmagicLegendDef[]) {
    this.legends$.next(
      arr_map(values, {
        transformer: (value) => ({
          ...value,
          color: fallback(value.color, {
            to: () => '#868e96',
          }),
          default_count: fallback(value.default_count, {
            to: () => 0,
          }),
          text_color: '#ffff',
          key: fallback(value.key, {
            to: () =>
              str_snake_case(value.label, {
                lowercase: true,
              }),
          }),
        }),
      }),
    );
  }

  @Input()
  set data(value: FieldmagicLegendData) {
    this._data$.next(value);
  }

  @Output('onLegendClick')
  readonly $onClick = new EventEmitter<FieldmagicLegendOnClickEvent>();

  private readonly _data$ = new BehaviorSubject<FieldmagicLegendData>({});

  private _onDataChanged?: Subscription;

  ngOnInit(): void {
    this._onDataChanged = this._data$
      .pipe(distinctUntilChanged(), debounceTime(200))
      .subscribe((data) => {
        const legends = this.legends$.value;

        this.legends$.next(
          arr_map(legends, {
            transformer: (legend) => {
              const datum: FieldmagicLegendData[string] = data_get(
                data,
                legend.key,
              );

              return {
                ...legend,
                count: fallback(data_get(datum, 'count'), {
                  to: () => 0,
                }),
                data: data_get(datum, 'data'),
              };
            },
          }),
        );
      });
  }

  ngOnDestroy(): void {
    if (this._onDataChanged) {
      this._onDataChanged.unsubscribe();
    }
  }

  tracker = (_: number, legend: FieldmagicInternalLegendDef) => legend.key;

  onClick(legend: FieldmagicInternalLegendDef): void {
    this.$onClick.emit({
      key: legend.key,
      data: legend.data,
    });
  }
}

export type FieldmagicLegendDef = {
  label: string;
  key?: string;
  color?: string;
  default_count?: number;
};

export type FieldmagicLegendData = {
  [key: string]: {
    count: number;
    data?: OptionalValue<Record<string, any>>;
  };
};

export type FieldmagicLegendOnClickEvent<D = Record<string, any>> = {
  key: string;
  data?: OptionalValue<D>;
};

type FieldmagicInternalLegendDef = FieldmagicLegendDef & {
  key: string;
  text_color?: string;
  count?: number;
  data?: OptionalValue<Record<string, any>>;
};
